import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, KeyRound, Mail, Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { homeForGroups } from '../../auth/routing';
import { apiFetch } from '../../api/client';
import { isValidPin } from '../../auth/pinMapping';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import ConfirmAccount from './ConfirmAccount';
import loginImage from '../../assets/login image bhima.png';
import bhimaLogo from '../../assets/bhima-logo.svg';
import styles from '../../styles/authShell.module.css';
import signupStyles from './Signup.module.css';

const STEPS = { DETAILS: 'details', CONFIRM: 'confirm', ADDRESS: 'address' };

export default function Signup() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.DETAILS);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);

  const [address, setAddress] = useState({ line1: '', line2: '', city: '', state: '', pincode: '' });
  const [addressNotice, setAddressNotice] = useState('');

  const phone = `+91${phoneDigits}`;

  const goHome = (s) => {
    const home = homeForGroups(s.groups);
    navigate(home || '/login', { replace: true });
  };

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (phoneDigits.length !== 10) {
      setError('Enter a valid 10-digit phone number.');
      return;
    }
    if (!isValidPin(pin)) {
      setError('PIN must be exactly 4 digits.');
      return;
    }
    if (pin !== confirmPin) {
      setError('PINs do not match.');
      return;
    }
    setLoading(true);
    try {
      await signUp({ phone, email, pin });
      setStep(STEPS.CONFIRM);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmed = (newSession) => {
    setSession(newSession);
    setStep(STEPS.ADDRESS);
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAddressNotice('');
    try {
      await apiFetch('/users/me/addresses', {
        method: 'PUT',
        token: session.idToken,
        body: { ...address, is_default: true },
      });
      goHome(session);
    } catch {
      setAddressNotice("Couldn't save that address — you can add it later from your profile.");
      setTimeout(() => goHome(session), 1600);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.panel}>
        <img src={loginImage} alt="" className={styles.panelImage} />
      </div>

      <div className={styles.formSide}>
        {step === STEPS.DETAILS && (
          <form className={styles.form} onSubmit={handleDetailsSubmit}>
            <img src={bhimaLogo} alt="Bhima Store" className={styles.mobileLogo} />
            <p className={styles.eyebrow}>Create your account</p>
            <h2 className={styles.formTitle}>Join Bhima Store</h2>
            <p className={styles.formSubtitle}>Phone and email are both required — you can sign in with either.</p>

            <AuthMessage>{error}</AuthMessage>

            <label className={styles.field}>
              <span>Phone number</span>
              <div className={styles.inputWrap}>
                <Phone size={15} />
                <span className={signupStyles.prefix}>+91</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  required
                  value={phoneDigits}
                  onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="98765 43210"
                />
              </div>
            </label>

            <label className={styles.field}>
              <span>Email</span>
              <div className={styles.inputWrap}>
                <Mail size={15} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
            </label>

            <label className={styles.field}>
              <span>Choose a 4-digit PIN</span>
              <div className={styles.inputWrap}>
                <KeyRound size={15} />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  required
                  className={styles.pinInput}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="••••"
                />
              </div>
            </label>

            <label className={styles.field}>
              <span>Confirm PIN</span>
              <div className={styles.inputWrap}>
                <KeyRound size={15} />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  required
                  className={styles.pinInput}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="••••"
                />
              </div>
            </label>

            <Button
              type="submit"
              size="lg"
              icon={ArrowRight}
              iconPosition="right"
              disabled={loading}
              className={styles.submit}
            >
              {loading ? 'Creating account…' : 'Continue'}
            </Button>

            <p className={styles.helper}>
              Already have an account? <Link to="/login" className={styles.linkBtn}>Sign in</Link>
            </p>
          </form>
        )}

        {step === STEPS.CONFIRM && (
          <ConfirmAccount initialPhone={phone} initialPin={pin} onDone={handleConfirmed} />
        )}

        {step === STEPS.ADDRESS && (
          <form className={styles.form} onSubmit={handleAddressSubmit}>
            <p className={styles.eyebrow}>Almost done</p>
            <h2 className={styles.formTitle}>Add a delivery address</h2>
            <p className={styles.formSubtitle}>You can skip this and add it later from your profile.</p>

            <AuthMessage>{addressNotice}</AuthMessage>

            <label className={styles.field}>
              <span>Address line 1</span>
              <div className={styles.inputWrap}>
                <input
                  required
                  value={address.line1}
                  onChange={(e) => setAddress((a) => ({ ...a, line1: e.target.value }))}
                  placeholder="House no, street"
                />
              </div>
            </label>
            <label className={styles.field}>
              <span>Address line 2 (optional)</span>
              <div className={styles.inputWrap}>
                <input
                  value={address.line2}
                  onChange={(e) => setAddress((a) => ({ ...a, line2: e.target.value }))}
                  placeholder="Landmark, area"
                />
              </div>
            </label>
            <label className={styles.field}>
              <span>City</span>
              <div className={styles.inputWrap}>
                <input required value={address.city} onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))} />
              </div>
            </label>
            <label className={styles.field}>
              <span>State</span>
              <div className={styles.inputWrap}>
                <input required value={address.state} onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))} />
              </div>
            </label>
            <label className={styles.field}>
              <span>Pincode</span>
              <div className={styles.inputWrap}>
                <input
                  required
                  inputMode="numeric"
                  maxLength={6}
                  value={address.pincode}
                  onChange={(e) => setAddress((a) => ({ ...a, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                />
              </div>
            </label>

            <Button type="submit" size="lg" disabled={loading} className={styles.submit}>
              {loading ? 'Saving…' : 'Save address'}
            </Button>
            <button type="button" className={signupStyles.skipBtn} onClick={() => goHome(session)}>
              Skip for now
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
